import { useEffect, useState } from "react";
import type { CartItem } from "../context/CartContext";

const STORAGE_KEY = "posCart";

export function useCustomerCart() {
  const [items, setItems] = useState<CartItem[]>([]);

  const loadCart = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CartItem[];
        setItems(parsed);
      } catch {
        // Invalid data, clear it
        localStorage.removeItem(STORAGE_KEY);
        setItems([]);
      }
    } else {
      setItems([]);
    }
  };

  useEffect(() => {
    // Load initial cart data
    loadCart();

    // Listen for storage events from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        loadCart();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events (for same-origin updates)
    // This is needed because storage event only fires in other tabs, not the current tab
    const handleCustomStorageChange = () => {
      loadCart();
    };

    // Listen for custom event that can be dispatched from the same tab
    window.addEventListener("cart-updated", handleCustomStorageChange);

    // Polling fallback (check every 500ms as backup)
    const interval = setInterval(() => {
      loadCart();
    }, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("cart-updated", handleCustomStorageChange);
      clearInterval(interval);
    };
  }, []);

  const total = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    items,
    total,
    itemCount,
  };
}
